/**
 * Voice provider registry — feature detection + fallback selection.
 *
 * CRITICAL INVARIANT (security): this registry NEVER instantiates a CLOUD ASR
 * provider, and never originates an audio/network call. PR5 ships only the
 * on-device {@link WebSpeechRecognitionProvider} (the browser engine may use a
 * server internally, but THIS code path opens no socket). The Whisper / TFJS
 * providers are inert stubs (`isSupported() === false`) referenced only so the
 * Phase 2/3 wiring has a typed seam. Because we test `isSupported()` before
 * instantiating, and the stubs report false, no cloud provider is ever built.
 *
 * Pure: no React, no store, no `fetch`. The only browser API touched here is
 * `navigator.permissions` / `navigator.mediaDevices` for the mic check, which
 * is a permission probe, NOT an audio upload.
 */
import {
  type MicrophonePermissionState,
  type SpeechRecognitionProvider,
  type SpeechSynthesisProvider,
} from './types'
import { NativeCapacitorRecognitionProvider } from './native-capacitor-provider'
import { WebSpeechRecognitionProvider } from './web-speech-provider'
import { WebSpeechSynthesisProvider } from './web-speech-synthesis-provider'

/**
 * Factory entry. We store FACTORIES, not instances, so a provider is only
 * constructed after its `isSupported()` gate passes — guaranteeing the stubs
 * (and any future cloud provider) are never instantiated when unsupported.
 */
interface RecognitionProviderEntry {
  id: string
  /** `true` when this provider keeps audio on-device / never calls our network. */
  readonly local: true
  create: () => SpeechRecognitionProvider
}

/**
 * ASR fallback chain, highest preference first: native-capacitor → web-speech.
 * ALL entries are local-only (OS / browser engine, never a cloud provider of ours).
 * Cloud providers are intentionally absent — adding one here would violate the
 * registry invariant and is blocked by {@link registry.test.ts}.
 *
 * Note: the Phase 2/3 stubs are reachable as factories, but each stub's
 * `isSupported()` returns false, so `getRecognitionProvider()` skips them.
 */
const RECOGNITION_CHAIN: RecognitionProviderEntry[] = [
  // Inside the Capacitor app the WebView has no Web Speech API: use the OS
  // recognizer. Not a cloud provider of ours (no socket from this code path).
  { id: 'native-capacitor', local: true, create: () => new NativeCapacitorRecognitionProvider() },
  { id: 'web-speech', local: true, create: () => new WebSpeechRecognitionProvider() },
]

/**
 * Probe-only check: does ANY local recognition provider work here? Constructs
 * a provider lazily, calls `isSupported()`, and discards it.
 */
export function isSpeechRecognitionSupported(): boolean {
  return RECOGNITION_CHAIN.some((entry) => entry.create().isSupported())
}

/** Probe-only check for TTS support. */
export function isSpeechSynthesisSupported(): boolean {
  return new WebSpeechSynthesisProvider().isSupported()
}

/**
 * Resolve the best-supported, LOCAL ASR provider, or `null` when none works in
 * this environment (e.g. Firefox, SSR). Never returns a cloud provider.
 */
export function getRecognitionProvider(): SpeechRecognitionProvider | null {
  for (const entry of RECOGNITION_CHAIN) {
    const provider = entry.create()
    if (provider.isSupported()) return provider
  }
  return null
}

/** Resolve the TTS provider, or `null` when synthesis is unavailable. */
export function getSynthesisProvider(): SpeechSynthesisProvider | null {
  const provider = new WebSpeechSynthesisProvider()
  return provider.isSupported() ? provider : null
}

/**
 * Probe the microphone permission WITHOUT capturing audio. Prefers the
 * Permissions API (no prompt, no stream); returns `'unknown'` when neither
 * the Permissions API nor a navigator is available. This NEVER calls
 * `getUserMedia` — it must not trigger a capture or a prompt as a side effect
 * of a status check.
 */
export async function requestMicrophonePermission(): Promise<MicrophonePermissionState> {
  if (typeof navigator === 'undefined') return 'unknown'

  const permissions = navigator.permissions
  if (!permissions || typeof permissions.query !== 'function') return 'unknown'

  try {
    // `microphone` is not in the standard PermissionName union in all TS DOM
    // versions; cast through the spec'd string without using `any`.
    const status = await permissions.query({
      name: 'microphone' as PermissionName,
    })
    switch (status.state) {
      case 'granted':
        return 'granted'
      case 'denied':
        return 'denied'
      default:
        return 'prompt'
    }
  } catch {
    // Browser doesn't support querying `microphone` — caller must fall back to
    // an actual capture attempt elsewhere (outside this pure layer).
    return 'unknown'
  }
}
